'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'

import { Icon, IconComponent } from '@graftcode/design-system'
import clsx from 'clsx'
import { useShallow } from 'zustand/react/shallow'

import { selectGraftcodeOnboardingTutorialGuide } from '@apps/graftcode-onboarding/stores/graftcode-onboarding.selector'
import { useGraftcodeOnboardingStore } from '@apps/graftcode-onboarding/stores/graftcode-onboarding.store'

import {
  GRAFTCODE_ONBOARDING_SCENARIO,
  GRAFTCODE_ONBOARDING_SCENARIOS,
  GRAFTCODE_ONBOARDING_SETUP_MODE,
  resolveScenarioContentPath,
} from '../../graftcode-onboarding.const'
import { GRAFTCODE_ONBOARDING_TEST_IDS } from '../../graftcode-onboarding-test-ids.const'
import { useGraftcodeOnboardingGuideMotion } from '../../hooks/useGraftcodeOnboardingGuideMotion'
import { useOnboardingTutorialContent } from '../../hooks/useOnboardingTutorialContent'
import { getOnboardingStacksForScenario } from '../../lib/buildOnboardingStackOptions'
import { getVisibleTutorialStepIndexes } from '../../lib/getVisibleTutorialStepIndexes'
import { getOnboardingRequirementChips } from '../../lib/graftcode-onboarding-requirements'
import { prefetchOnboardingTutorialMarkdownFireAndForget } from '../../lib/onboarding-tutorial-content-cache'
import { scrollGraftcodeOnboardingToTop } from '../../lib/scrollGraftcodeOnboardingToTop'

import { splitDescription } from './graftcode-onboarding-dashboard.helpers'
import { GraftcodeOnboardingDashboardTutorialSteps } from './GraftcodeOnboardingDashboardTutorialSteps'
import { GraftcodeOnboardingOsPicker } from './GraftcodeOnboardingOsPicker'
import { GraftcodeOnboardingStackPicker } from './GraftcodeOnboardingStackPicker'
import { GraftcodeOnboardingTutorialVideo } from './GraftcodeOnboardingTutorialVideo'

import styles from './GraftcodeOnboardingDashboard.module.scss'

export function GraftcodeOnboardingDashboard() {
  const t = useTranslations('graftcodeOnboarding')
  const {
    scenarioId,
    selectedStackId,
    setupMode,
    setSetupMode,
    setSelectedStackId,
    activeStepIndex,
    setActiveStepIndex,
    completeTutorial,
    tutorialCompleted,
    tutorialVideoHidden,
    reopenTutorialVideo,
    reopenWizardFromDashboard,
    guideOs,
    setGuideOs,
  } = useGraftcodeOnboardingStore(useShallow(selectGraftcodeOnboardingTutorialGuide))

  const stacks = useMemo(
    () => (scenarioId ? getOnboardingStacksForScenario(scenarioId, setupMode) : []),
    [scenarioId, setupMode]
  )

  useEffect(() => {
    if (!stacks.length || !selectedStackId) {
      return
    }
    const fallbackStackId = stacks[0]?.id
    if (fallbackStackId && !stacks.some((stack) => stack.id === selectedStackId)) {
      setSelectedStackId(fallbackStackId)
    }
  }, [stacks, selectedStackId, setSelectedStackId])

  const { data, loading, error, readyFromCache, resolvedContentKey } = useOnboardingTutorialContent(
    scenarioId,
    selectedStackId,
    setupMode,
    guideOs
  )

  const prefetchTutorial = useCallback(
    (stackId: string | null, mode: typeof setupMode) => {
      if (!scenarioId || !stackId) {
        return
      }
      prefetchOnboardingTutorialMarkdownFireAndForget(resolveScenarioContentPath(scenarioId, stackId, mode))
    },
    [scenarioId]
  )

  useEffect(() => {
    if (!scenarioId || !selectedStackId) {
      return
    }
    prefetchTutorial(selectedStackId, setupMode)
    const alternateMode =
      setupMode === GRAFTCODE_ONBOARDING_SETUP_MODE.agent
        ? GRAFTCODE_ONBOARDING_SETUP_MODE.manual
        : GRAFTCODE_ONBOARDING_SETUP_MODE.agent
    prefetchTutorial(selectedStackId, alternateMode)
    for (const stack of stacks) {
      prefetchTutorial(stack.id, setupMode)
    }
  }, [scenarioId, selectedStackId, setupMode, stacks, prefetchTutorial])

  const stepsReady = Boolean(!error && scenarioId && (data?.steps?.length ?? 0) > 0)
  const [shellEnterClass, setShellEnterClass] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!stepsReady || shellEnterClass) {
      return
    }
    setShellEnterClass(readyFromCache ? styles.rootGuideInstant : styles.rootGuideEnter)
  }, [stepsReady, shellEnterClass, readyFromCache])

  const guideConfigKey = `${scenarioId ?? ''}:${selectedStackId}:${setupMode}`
  const { stepMotion, beginStepForward, beginStepBack } = useGraftcodeOnboardingGuideMotion({
    activeStepIndex,
    stepsReady,
    configKey: guideConfigKey,
    contentRefreshing: loading,
    instantEnter: readyFromCache,
  })

  const handleCompleteTutorial = useCallback(() => {
    completeTutorial()
    scrollGraftcodeOnboardingToTop()
  }, [completeTutorial])

  if (!scenarioId) {
    return null
  }

  const scenarioMeta = GRAFTCODE_ONBOARDING_SCENARIOS.find((item) => item.id === scenarioId)
  const steps = data?.steps ?? []
  const visibleStepIndexes = getVisibleTutorialStepIndexes(activeStepIndex, steps.length)
  const isLastStep = activeStepIndex >= steps.length - 1
  const requirementChips = getOnboardingRequirementChips(scenarioId, selectedStackId)
  const [descriptionLead, descriptionTail] = splitDescription(data?.description ?? '')
  const guideTitle = scenarioMeta ? t(scenarioMeta.labelKey) : (data?.title ?? '')

  const handleNext = () => {
    if (isLastStep) {
      return
    }
    beginStepForward()
    setActiveStepIndex(activeStepIndex + 1)
  }

  const handlePrevious = () => {
    if (activeStepIndex > 0) {
      beginStepBack()
      setActiveStepIndex(activeStepIndex - 1)
    }
  }

  const showTutorialButtonOnStep = (stepIndex: number) => stepIndex === activeStepIndex && tutorialVideoHidden

  return (
    <div className={styles.root} data-testid={GRAFTCODE_ONBOARDING_TEST_IDS.dashboard}>
      {!tutorialCompleted ? (
        <>
          <div className={clsx(styles.guideShell, shellEnterClass)}>
            <button type="button" className={styles.backButton} onClick={reopenWizardFromDashboard}>
              <IconComponent icon={Icon.ChevronLeftRegular} size={16} />
              {t('dashboard.backToGoals')}
            </button>

            <div className={styles.hero}>
              <span className={styles.heroIcon} aria-hidden>
                {scenarioMeta ? <Image src={scenarioMeta.icon} alt="" width={24} height={24} /> : null}
              </span>
              <div className={styles.heroCopy} key={resolvedContentKey}>
                <h2 className={styles.heroTitle}>{guideTitle}</h2>
                <p className={styles.heroDescription}>{descriptionLead || data?.goalText}</p>
                {descriptionTail ? <p className={styles.heroDescription}>{descriptionTail}</p> : null}
              </div>
            </div>

            <div className={styles.stickyBar}>
              <div className={styles.setupRow}>
                <div className={styles.segmented} role="tablist" aria-label={t('dashboard.setupLabel')}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={setupMode === GRAFTCODE_ONBOARDING_SETUP_MODE.manual}
                    className={clsx(
                      styles.segment,
                      setupMode === GRAFTCODE_ONBOARDING_SETUP_MODE.manual && styles.segmentActive
                    )}
                    onClick={() => {
                      prefetchTutorial(selectedStackId, GRAFTCODE_ONBOARDING_SETUP_MODE.manual)
                      setSetupMode(GRAFTCODE_ONBOARDING_SETUP_MODE.manual)
                    }}
                  >
                    {t('dashboard.manualSetup')}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={setupMode === GRAFTCODE_ONBOARDING_SETUP_MODE.agent}
                    className={clsx(
                      styles.segment,
                      setupMode === GRAFTCODE_ONBOARDING_SETUP_MODE.agent && styles.segmentActive
                    )}
                    onClick={() => {
                      prefetchTutorial(selectedStackId, GRAFTCODE_ONBOARDING_SETUP_MODE.agent)
                      setSetupMode(GRAFTCODE_ONBOARDING_SETUP_MODE.agent)
                    }}
                  >
                    {t('dashboard.agentSetup')}
                  </button>
                </div>
                <GraftcodeOnboardingOsPicker guideOs={guideOs} onChange={setGuideOs} />
              </div>

              {stacks.length > 1 ? (
                <div className={styles.stackPickerSection}>
                  <GraftcodeOnboardingStackPicker
                    stacks={stacks}
                    selectedStackId={selectedStackId}
                    onSelect={(stackId) => {
                      prefetchTutorial(stackId, setupMode)
                      setSelectedStackId(stackId)
                    }}
                    onPrefetchStack={(stackId) => prefetchTutorial(stackId, setupMode)}
                    ariaLabel={
                      scenarioId === GRAFTCODE_ONBOARDING_SCENARIO.frontend
                        ? t('dashboard.frameworkPickerLabel')
                        : t('dashboard.programmingLanguagePickerLabel')
                    }
                    scrollBackLabel={t('dashboard.scrollStacksBack')}
                    scrollForwardLabel={t('dashboard.scrollStacksForward')}
                  />
                </div>
              ) : null}
            </div>
          </div>

          <div className={clsx(styles.guideBody, loading && data && styles.guideBodyLoading)}>
            {loading && !data ? <p className={styles.loading}>{t('dashboard.loading')}</p> : null}
            {error ? <p className={styles.error}>{error}</p> : null}

            {!error && steps.length > 0 ? (
              <GraftcodeOnboardingDashboardTutorialSteps
                steps={steps}
                visibleStepIndexes={visibleStepIndexes}
                activeStepIndex={activeStepIndex}
                setupMode={setupMode}
                requirementChips={requirementChips}
                showTutorialButtonOnStep={showTutorialButtonOnStep}
                stepMotion={stepMotion}
                contentRefreshing={loading}
                onPrevious={handlePrevious}
                onNext={handleNext}
                onCompleteTutorial={handleCompleteTutorial}
                onReopenTutorialVideo={reopenTutorialVideo}
              />
            ) : null}
          </div>

          <GraftcodeOnboardingTutorialVideo video={data?.tutorialVideo ?? null} title={guideTitle} />
        </>
      ) : null}
    </div>
  )
}
